import { memo, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import ScrollToTop from "./components/ScrollToTop";
import ScrollTopButton from "./components/ScrollTopButton";
import ReadingProgress from "./components/ReadingProgress";
import ErrorBoundary from "./components/ErrorBoundary";
import ChatWidget from "./components/ChatWidget";
import { QuoteFormProvider } from "./components/QuoteFormContext";
import { routes } from "./routes";

const RouteFallback = () => (
  <div style={{ minHeight: "100svh" }} aria-hidden="true" />
);

/**
 * The route island. Memoised with no props so nothing above it can re-render
 * the Suspense boundary while it is still hydrating — an update to a
 * dehydrated boundary makes React discard the server HTML and client-render.
 */
const Pages = memo(() => (
  <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {routes.map(({ path, Page }) => (
          <Route key={path} path={path} element={<Page />} />
        ))}
      </Routes>
    </Suspense>
  </ErrorBoundary>
));
Pages.displayName = "Pages";

/**
 * Everything inside the router. Rendered by `BrowserRouter` in the browser and
 * by `StaticRouter` in the build-time prerender (src/entry-server.tsx).
 *
 * `LazyMotion` + `m.*` components load only the DOM-animation feature set of
 * framer-motion instead of the full bundle; `strict` throws if a full `motion.*`
 * component slips back in.
 */
export const AppShell = () => (
  <LazyMotion features={domAnimation} strict>
    <QuoteFormProvider>
      <ScrollToTop />
      <ReadingProgress />
      <Pages />
      <ScrollTopButton />
      <ChatWidget />
    </QuoteFormProvider>
  </LazyMotion>
);

const App = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

export default App;
